import { createSlice } from '@reduxjs/toolkit';
import type {PayloadAction} from "@reduxjs/toolkit"
import { VisageEditorWindowModes, IDisplayDeviceInfo,  IVisageEditorSettings, IVisageTemplate, IGrapesJSProjectData, defaultDevice, CssStates, IVisageSaveData, IVisageEditorContext, IVisageBlockDefinitions, IVisageComponentDefinition } from '../../interfaces/VisageUI';
import { generateDefaultVisageTemplates } from '../../components/visageUI/defaultTemplates/DefaultTemplates';
import { assert } from '../../classes/utilities/ErrorHandling';
import { JsonCopy } from '../../classes/utilities/Misc';
import { upgradeGrapesProject } from '../../components/visageUI/GrapesProjectFormatUpgrader';
import { CustomEventTypes } from '../../globals';
import { TemplateSortModes } from '../../interfaces/enums';

type VisageState = {
	stepUIData: Record<string, IGrapesJSProjectData>;
	stepTemplateMap: Record<string, string>; // stepid -> templateid
	currentDisplayDevice: IDisplayDeviceInfo;
	uiPreviewScale: number;
	editorMode: VisageEditorWindowModes;
	editorSettings: IVisageEditorSettings;
	elementState: CssStates,
	templateToEdit: string;
	templateEditorTargetStep: string;
	uiTemplateData: Record<string, IVisageTemplate>;
	cameraCanvasPairings: Record<string, string[]>;
	clipboard: IVisageComponentDefinition[];
	reload: number;
	selectedComponentIds: string[];
	editorContext: IVisageEditorContext;
	blockDefinitions: IVisageBlockDefinitions;
	templateSort: TemplateSortModes;
	showOverlayMenu: boolean;
};

export const visageSliceInitialState: VisageState = {
	stepUIData: {},
	stepTemplateMap: {},
	currentDisplayDevice: defaultDevice(),
	uiPreviewScale: 1,
	editorMode: VisageEditorWindowModes.Step,
	editorSettings: {
		fitCanvas: true,
		canvasScale: 1,
		showViewport: false,
		automaticMode: true,
	},
	elementState: CssStates.Normal,
	templateToEdit: "",
	templateEditorTargetStep: "",
	uiTemplateData: {},
	cameraCanvasPairings: {},
	clipboard: [],
	reload: 0,
	selectedComponentIds: [],
	editorContext: {
		projectId: "",
		projectData: {} as IGrapesJSProjectData,
		components: [],
		componentStyles: {},
	},
	blockDefinitions: {},
	templateSort: TemplateSortModes.Alphabetical,
	showOverlayMenu: false,
};

const VisageSlice = createSlice({
	name: "visage",
	initialState: visageSliceInitialState,
	reducers: {
		loadUIData(state, action: PayloadAction<IVisageSaveData>)
		{
			state.uiTemplateData = action.payload?.uiTemplateData ? JsonCopy(action.payload.uiTemplateData) : {};
			state.stepTemplateMap = action.payload?.stepTemplateMap ? JsonCopy(action.payload.stepTemplateMap) : {};
			state.cameraCanvasPairings = action.payload?.cameraCanvasPairings ? JsonCopy(action.payload.cameraCanvasPairings) : {};
			state.stepUIData = action.payload?.stepUIData ? JsonCopy(action.payload.stepUIData) : {};

			// if no templates are defined, create the default ones
			if (!Object.keys(state.uiTemplateData).length)
			{
				state.uiTemplateData = { ...generateDefaultVisageTemplates() };
			}
			
			// fix issues with the grapes project data
			for(const [templateId, templateData] of Object.entries<IVisageTemplate>(state.uiTemplateData))
			{
				const fixedTemplate = JsonCopy<IVisageTemplate>(templateData ?? {});
				upgradeGrapesProject(fixedTemplate.content);

				// always start with the current state as the previous state
				fixedTemplate.previousState = JsonCopy(fixedTemplate.content);

				// save the fixed template
				state.uiTemplateData[templateId] = fixedTemplate;
			}

			// clear out empty step ui data
			for(const stepId of Object.keys(state.stepUIData))
			{
				if(Object.keys(state.stepUIData[stepId]).length === 0)
				{
					delete state.stepUIData[stepId];
				}
				else
				{
					// fix text components
					const stepData = JsonCopy(state.stepUIData[stepId]);
					upgradeGrapesProject(stepData);
					state.stepUIData[stepId] = stepData
				}
			}
		},
		setStepUIData(state, action: PayloadAction<{stepId: string, data: IGrapesJSProjectData}>)
		{
			state.stepUIData[action.payload.stepId] = {...action.payload.data};
		},
		reset(state, action: PayloadAction<undefined>)
		{
			return visageSliceInitialState;
		},
		clearUIData(state, action: PayloadAction<undefined>)
		{
			state.stepUIData = {};
			state.stepTemplateMap = {};
			state.uiTemplateData = {};
			state.cameraCanvasPairings = {};
			state.editorMode = VisageEditorWindowModes.Step;
			state.templateToEdit = "";
			state.templateEditorTargetStep = "";
			state.clipboard = [];
			state.selectedComponentIds = [];
			state.editorContext = {
				projectId: "",
				projectData: {} as IGrapesJSProjectData,
				components: [],
				componentStyles: {},
			};
			state.showOverlayMenu = false;
		},
		setCurrentDisplayDevice(state, action: PayloadAction<IDisplayDeviceInfo | undefined>)
		{
			state.currentDisplayDevice = action.payload ?? defaultDevice();
			document.dispatchEvent(new CustomEvent(CustomEventTypes.saveWlcSettings));
		},
		setUIPreviewScale(state, action: PayloadAction<number>)
		{
			state.uiPreviewScale = action.payload;
		},
		setEditorSettings(state, action:PayloadAction<Partial<IVisageEditorSettings>>)
		{
			state.editorSettings = { ...state.editorSettings, ...action.payload};
			document.dispatchEvent(new CustomEvent(CustomEventTypes.saveWlcSettings));
		},
		setUITemplateData(state, action: PayloadAction<{templateId: string, data: IVisageTemplate}>)
		{
			state.uiTemplateData[action.payload.templateId] = { ...action.payload.data };
		},
		deleteUITemplateData(state, action: PayloadAction<{templateId: string}>)
		{
			delete state.uiTemplateData[action.payload.templateId];
		},
		setStepTemplate(state, action: PayloadAction<{stepId: string, templateId: string}>)
		{
			const { stepId, templateId } = action.payload;

			// set mapping
			state.stepTemplateMap[stepId] = templateId;

			// copy template data over step ui data
			VisageSlice.caseReducers.setStepUIData(state, { payload: { stepId: stepId, data: { ...state.uiTemplateData[templateId]?.content } }, type: "setStepUIData" });
		},
		setTemplateEditTarget(state, action: PayloadAction<string>)
		{
			state.templateToEdit = action.payload; // action.payload is the templateId
		},
		setEditorMode(state, action: PayloadAction<VisageEditorWindowModes>)
		{
			state.editorMode = action.payload;
		},
		setTemplatePreviousState(state, action: PayloadAction<{ templateId: string, state: IGrapesJSProjectData }>)
		{
			assert(state.uiTemplateData[action.payload.templateId] !== undefined, `Template with id ${action.payload.templateId} does not exist`);
			state.uiTemplateData[action.payload.templateId].previousState = { ...action.payload.state };
		},
		setCameraCanvasPairing(state, action: PayloadAction<{ cameraId: string, canvasId: string }>)
		{
			if (!state.cameraCanvasPairings[action.payload.cameraId])
			{
				state.cameraCanvasPairings[action.payload.cameraId] = [action.payload.canvasId];
				return;
			}

			// only once per canvas
			if (state.cameraCanvasPairings[action.payload.cameraId].includes(action.payload.canvasId)) return;
			else state.cameraCanvasPairings[action.payload.cameraId].push(action.payload.canvasId);
		},
		removeCameraCanvasPairing(state, action: PayloadAction<{ cameraId: string, canvasId: string }>)
		{
			if (!state.cameraCanvasPairings[action.payload.cameraId]) return;

			state.cameraCanvasPairings[action.payload.cameraId] = state.cameraCanvasPairings[action.payload.cameraId].filter((canvasId) => canvasId !== action.payload.canvasId);
			
			// remove the camera entry if there are no more pairings
			if(!state.cameraCanvasPairings[action.payload.cameraId].length)
			{
				delete state.cameraCanvasPairings[action.payload.cameraId];
			}
		},
		setTemplateEditorTargetStep(state, action: PayloadAction<string>)
		{
			state.templateEditorTargetStep = action.payload;
		},
		setClipboard(state, action: PayloadAction<IVisageComponentDefinition[]>)
		{
			state.clipboard = action.payload;
		},
		setElementState(state, action: PayloadAction<CssStates>)
		{
			state.elementState = action.payload;
		},
		reload(state, action: PayloadAction<undefined>)
		{
			state.reload++;
		},
		setSelectedComponentIds(state, action: PayloadAction<string[]>)
		{
			state.selectedComponentIds = action.payload;
		},
		setEditorContext(state, action: PayloadAction<Partial<IVisageEditorContext>>)
		{
			state.editorContext = { ...state.editorContext, ...action.payload };
		},
		setBlockDefinitions(state, action: PayloadAction<IVisageBlockDefinitions>)
		{
			state.blockDefinitions = action.payload;
		},
		setTemplateSort(state, action: PayloadAction<TemplateSortModes>)
		{
			state.templateSort = action.payload;
		},
		setShowOverlayMenu(state, action: PayloadAction<boolean>)
		{
			state.showOverlayMenu = action.payload;
		},
	},
});

export default VisageSlice;